import React from 'react';
import { Card, CardContent, Typography, Box, Chip, Tooltip, IconButton } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import StarIcon from '@mui/icons-material/Star';
import { Draggable } from '@hello-pangea/dnd';
import { useNavigate } from 'react-router-dom';
import { getIdleUrgency, getUrgencyColor, getDaysSinceActivity } from './pipelineConfig';

const KanbanCard = ({ lead, index }) => {
  const navigate = useNavigate();
  const urgency = getIdleUrgency(lead.lastActionDate);
  const urgencyColor = getUrgencyColor(urgency);

  const handleClick = () => {
    navigate(`/dashboard/lead/view/${lead._id}`);
  };

  const agentName = lead.assigned_agent
    ? `${lead.assigned_agent.firstName || ''} ${lead.assigned_agent.lastName || ''}`.trim()
    : 'Unassigned';

  return (
    <Draggable draggableId={lead._id} index={index}>
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          sx={{
            mb: 1,
            borderLeft: `4px solid ${urgencyColor}`,
            cursor: 'grab',
            bgcolor: snapshot.isDragging ? 'action.hover' : 'background.paper',
            boxShadow: snapshot.isDragging ? 6 : 1,
            transition: 'box-shadow 0.2s ease',
            '&:hover': {
              boxShadow: 3,
            },
          }}
        >
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            {/* Name row */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Typography
                variant="subtitle2"
                sx={{
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                  fontWeight: 600,
                  lineHeight: 1.3,
                }}
                onClick={handleClick}
              >
                {lead.firstName} {lead.lastName}
              </Typography>
              {/* Lead score */}
              {lead.leadScore > 0 && (
                <Tooltip title={`Score: ${lead.leadScore}`}>
                  <Box display="flex" alignItems="center" gap={0.25}>
                    <StarIcon sx={{ fontSize: 14, color: '#FFC107' }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {lead.leadScore}
                    </Typography>
                  </Box>
                </Tooltip>
              )}
            </Box>

            {/* Phone */}
            {lead.phoneNumber && (
              <Box display="flex" alignItems="center" mt={0.5} gap={0.5}>
                <PhoneIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary">
                  {lead.phoneNumber}
                </Typography>
              </Box>
            )}

            {/* Bottom row: source + urgency */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
              {lead.leadSource ? (
                <Chip
                  label={lead.leadSource.length > 18 ? `${lead.leadSource.slice(0, 18)}...` : lead.leadSource}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 20, maxWidth: 140 }}
                />
              ) : (
                <Box />
              )}
              <Tooltip title={getDaysSinceActivity(lead.lastActionDate)}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: urgencyColor,
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            </Box>

            {/* Agent name */}
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, fontSize: '0.65rem' }}>
              {agentName}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Draggable>
  );
};

export default React.memo(KanbanCard);
